import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EmailCaptureConfig, FormField } from '@/types/vsleads/emailCaptureTypes';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { User, Mail, Phone, Lock, Rows3, type LucideIcon } from 'lucide-react';

interface FormFieldsTabProps {
  form: UseFormReturn<EmailCaptureConfig>;
  isCapturePassword?: boolean;
}

interface ToggleRowProps {
  icon?: LucideIcon;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleRow({ icon: Icon, label, description, checked, onCheckedChange, disabled = false }: ToggleRowProps) {
  useLanguage();
  return (
    <div
      className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
        checked
          ? 'bg-[#F7F8FE] border-[#E2E8F0]'
          : 'bg-white border-[#E5E7EB]'
      }`}
    >
      <div className="flex-1 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            checked ? 'bg-[#EEF2FF]' : 'bg-[#F9FAFB]'
          }`}>
            {Icon && <Icon className={`h-5 w-5 ${checked ? 'text-[#2D6CF6]' : 'text-[#D1D5DB]'}`} />}
          </div>
          <div className="flex flex-col">
            <span className={`font-medium text-[15px] ${checked ? 'text-[#1A1A1A]' : 'text-[#9CA3AF]'}`}>
              {label}
            </span>
            <span className="text-[12px] text-[#6B7280] mt-0.5">
              {description}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className={`text-sm font-medium ${checked ? 'text-[#2D6CF6]' : 'text-[#9CA3AF]'}`}>
            {checked ? translateInterfaceText("Ativo") : translateInterfaceText("Inativo")}
          </span>
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            aria-label={label}
            className="data-[state=checked]:bg-[#3072FF]"
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

interface FieldItemProps {
  field: FormField;
  onToggleActive: (id: string, active: boolean) => void;
  isActive: boolean;
  disableToggle?: boolean;
}

function FieldItem({ field, onToggleActive, isActive, disableToggle = false }: FieldItemProps) {
  useLanguage();

  const getFieldLabel = (type: FormField['type'] | 'password') => {
    switch (type) {
      case 'name':
        return translateInterfaceText("Nome");
      case 'email':
        return 'E-mail';
      case 'phone':
        return 'WhatsApp';
      case 'password':
        return 'Senha';
      default:
        return type;
    }
  };

  const getFieldIcon = (type: FormField['type'] | 'password'): LucideIcon | undefined => {
    switch (type) {
      case 'name':
        return User;
      case 'email':
        return Mail;
      case 'phone':
        return Phone;
      case 'password':
        return Lock;
      default:
        return undefined;
    }
  };

  return (
    <ToggleRow
      icon={getFieldIcon(field.type)}
      label={getFieldLabel(field.type)}
      description={field.placeholder}
      checked={isActive}
      onCheckedChange={(checked) => onToggleActive(field.id, checked)}
      disabled={disableToggle}
    />
  );
}

export function FormFieldsTab({ form, isCapturePassword = false }: FormFieldsTabProps) {
  useLanguage();
  const fields = form.watch('fields');
  const orientation = form.watch('orientation');

  const toggleActive = (id: string, active: boolean) => {
    form.setValue(
      'fields',
      fields.map((f) => (f.id === id ? { ...f, active } : f)),
      { shouldDirty: true }
    );
  };

  // Campo "Senha" para captura por senha (sempre ativo)
  const passwordField: any = {
    id: 'senha',
    type: 'password',
    label: translateInterfaceText("Senha"),
    placeholder: translateInterfaceText("Digite a senha para acessar o vídeo"),
    required: true,
    active: true,
  };

  const displayFields = isCapturePassword ? [passwordField] : fields;

  return (
    <div className="space-y-6">
      {/* Lista de Campos */}
      <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
        <CardHeader className="pb-4">
          <CardTitle className="text-[17px] font-semibold text-[#101828]">
            {isCapturePassword ? translateInterfaceText("Campo de Senha") : translateInterfaceText("Campos do Formulário")}
          </CardTitle>
          <CardDescription className="text-[13px] text-[#667085]">
            {isCapturePassword
              ? translateInterfaceText("Campo obrigatório para proteção por senha")
              : translateInterfaceText("Ative ou desative os campos que deseja coletar no formulário")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {displayFields.map((field) => (
            <FieldItem
              key={field.id}
              field={field}
              onToggleActive={toggleActive}
              isActive={field.active !== false}
              disableToggle={isCapturePassword}
            />
          ))}
        </CardContent>
      </Card>

      {/* Layout do formulário (checkedCaptureVertical) — a captura por senha tem um campo só */}
      {!isCapturePassword && (
        <Card className="bg-white border border-[#E2E8F0] rounded-xl shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
          <CardHeader className="pb-4">
            <CardTitle className="text-[17px] font-semibold text-[#101828]">
              {translateInterfaceText("Layout do Formulário")}</CardTitle>
            <CardDescription className="text-[13px] text-[#667085]">
              {translateInterfaceText("Defina como os campos são exibidos no player")}</CardDescription>
          </CardHeader>
          <CardContent>
            <ToggleRow
              icon={Rows3}
              label={translateInterfaceText("Formulário vertical")}
              description={translateInterfaceText("Campos um abaixo do outro, com o botão em largura total")}
              checked={orientation === 'vertical'}
              onCheckedChange={(checked) =>
                form.setValue('orientation', checked ? 'vertical' : 'horizontal', { shouldDirty: true })
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
